/**
 * EventTriggeredCurriculum Component
 * Living School synchronized with the Creator's Clock
 * 
 * Displays event-triggered lessons that unlock based on:
 * 1. Biblical Mo'adim (Appointed Times)
 * 2. Pagan Holiday Exposures
 * 3. Celestial Events
 * 4. Identity Events
 */

import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import InvisibleTeacher from "./InvisibleTeacher";
import StandardLessonActions from "./StandardLessonActions";
import { isYoungLearnerGrade } from "../utils/gradeUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category styling
const CATEGORY_STYLES = {
  restoration: {
    bg: "linear-gradient(135deg, #1a3a1a 0%, #0a2a0a 100%)",
    border: "#90EE90",
    icon: "📜",
    label: "Restoration",
  },
  deception: {
    bg: "linear-gradient(135deg, #3a1a1a 0%, #2a0a0a 100%)",
    border: "#ff6b6b",
    icon: "🔍",
    label: "Deception Files",
  },
  luminaries: {
    bg: "linear-gradient(135deg, #1a1a3a 0%, #0a0a2a 100%)",
    border: "#87CEEB",
    icon: "☀️",
    label: "Luminaries",
  },
  identity: {
    bg: "linear-gradient(135deg, #3a2a1a 0%, #2a1a0a 100%)",
    border: "#FFD700",
    icon: "🗺️",
    label: "Identity Vault",
  },
};

// Urgency colors
const URGENCY_COLORS = {
  high: "#ff6b6b",
  medium: "#FFD700",
  normal: "#90EE90",
};

function EventTriggeredCurriculum({ gradeLevel = "6-8", onSelectLesson, onBack = null, backLabel = "← Back" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [lunarEvent, setLunarEvent] = useState(null);  // current Day's protocol card
  const [biblicalToday, setBiblicalToday] = useState(null);  // {day, month_name, gregorian, awaiting_spring_witness}

  // Fetch today's biblical date + lunar-event card data (data-driven by date).
  const fetchBiblicalToday = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const lat = 38.627, lon = -90.1994;
      try {
        await new Promise((res) => {
          if (!navigator.geolocation) return res();
          navigator.geolocation.getCurrentPosition(
            (p) => { /* override happens below */ res(p); },
            () => res(),
            { timeout: 4000, maximumAge: 600000 }
          );
        });
      } catch {}
      const r = await axios.get(`${API}/celestial/biblical/full-status`, {
        params: { latitude: lat, longitude: lon },
        headers: { Authorization: `Bearer ${token}` },
      });
      const sb = r.data?.sabbath || {};
      const date = r.data?.date || {};
      setLunarEvent(sb.lunar_event || null);
      setBiblicalToday({
        day: date.day,
        month_name: date.month_name,
        gregorian: new Date().toLocaleDateString("en-US", {
          month: "numeric", day: "numeric", year: "numeric",
        }),
        awaiting_spring_witness: !!date.awaiting_spring_witness,
        is_preparation: !!sb.is_preparation,
        gate_status: sb.gate_status,
      });
    } catch (e) {
      // Non-fatal — Living School falls back to the event list without the
      // protocol card. The user just doesn't see the Day-N callout.
    }
  }, []);

  // Fetch unlocked events
  const fetchUnlockedEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/event-curriculum/unlocked-events`,
        {
          params: { grade_level: gradeLevel },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setEvents(response.data.events || []);
      setCurrentDate(response.data.current_date);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch events:", err);
      setError("Unable to load curriculum events");
    } finally {
      setLoading(false);
    }
  }, [gradeLevel]);

  // Fetch lesson content
  const fetchLesson = async (eventId) => {
    setLessonLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/event-curriculum/lesson/${eventId}`,
        {
          params: { grade_level: gradeLevel },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setLessonContent(response.data);
      
      if (onSelectLesson) {
        onSelectLesson(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch lesson:", err);
      setError("Unable to load lesson content");
    } finally {
      setLessonLoading(false);
    }
  };

  useEffect(() => {
    fetchUnlockedEvents();
    fetchBiblicalToday();
    // Refresh every 30 minutes
    const interval = setInterval(fetchUnlockedEvents, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchUnlockedEvents]);

  // Handle event selection
  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    fetchLesson(event.id);
  };

  // Back to event list
  const handleBack = () => {
    setSelectedEvent(null);
    setLessonContent(null);
  };

  // Render loading state
  if (loading) {
    return (
      <div
        data-testid="event-curriculum-loading"
        style={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #0a0a1a 100%)",
          border: "2px solid #FFD700",
          borderRadius: "12px",
          padding: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: "10px" }}>📜</div>
        <p style={{ color: "#FFD700" }}>Loading Living Curriculum...</p>
        <p style={{ color: "#888", fontSize: "0.8rem" }}>
          Synchronizing with the Creator's Clock
        </p>
      </div>
    );
  }

  // Render error state
  if (error && events.length === 0) {
    return (
      <div
        style={{
          background: "#2a1a1a",
          border: "2px solid #ff6b6b",
          borderRadius: "12px",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <p style={{ color: "#ff6b6b" }}>{error}</p>
        <button
          onClick={fetchUnlockedEvents}
          style={{
            marginTop: "10px",
            padding: "8px 16px",
            background: "#ff6b6b",
            border: "none",
            borderRadius: "6px",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Render lesson generation indicator — shown after the user clicks an event
  // but BEFORE the backend returns the (possibly LLM-generated) lesson body.
  // First-time generation can take 5–30s; without this the user sees the
  // event list frozen and assumes the click did nothing.
  if (selectedEvent && lessonLoading && !lessonContent) {
    return (
      <div
        data-testid="lesson-generating-indicator"
        style={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #0a0a1a 100%)",
          border: "2px solid #FFD700",
          borderRadius: "12px",
          padding: "40px 30px",
          textAlign: "center",
          minHeight: "260px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "14px",
        }}
      >
        <div
          aria-hidden
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            border: "4px solid rgba(255,215,0,0.18)",
            borderTopColor: "#FFD700",
            animation: "spin 0.9s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <h3 style={{ color: "#FFD700", margin: 0, fontSize: "1.15rem" }}>
          Preparing your Living School lesson…
        </h3>
        <p style={{ color: "#ccc", margin: 0, fontSize: "0.9rem", maxWidth: "440px" }}>
          {selectedEvent.name}
        </p>
        <p style={{ color: "#888", margin: 0, fontSize: "0.8rem", maxWidth: "440px" }}>
          The Guide is composing grade-appropriate content from the primary
          sources. This can take a few seconds the first time an event is
          opened — subsequent visits load instantly.
        </p>
        <button
          data-testid="lesson-generating-cancel"
          onClick={handleBack}
          style={{
            marginTop: "8px",
            background: "transparent",
            color: "#CD853F",
            border: "1px solid #CD853F60",
            borderRadius: "6px",
            padding: "6px 14px",
            fontSize: "0.8rem",
            cursor: "pointer",
          }}
        >
          ← Cancel
        </button>
      </div>
    );
  }

  // Render lesson content view
  if (selectedEvent && lessonContent) {
    return (
      <>
        <LessonViewer
          lesson={lessonContent}
          event={selectedEvent}
          gradeLevel={gradeLevel}
          onBack={handleBack}
          loading={lessonLoading}
        />
        
        {/* THE INVISIBLE TEACHER - Voice-First AI for Living School.
            NOTE: Living-School lessons nest paragraphs under `content.paragraphs`
            (each with a `.text` field). The previous mapping read
            `lessonContent.paragraphs?.map(p => p.content)` — both keys wrong —
            and silently produced "", so InvisibleTeacher had nothing to teach. */}
        <InvisibleTeacher
          gradeLevel={gradeLevel}
          lessonContent={
            lessonContent.content?.full_text ||
            (lessonContent.content?.paragraphs || [])
              .map((p) => (p?.text || p?.content || "").trim())
              .filter(Boolean)
              .join("\n\n")
          }
          lessonTitle={selectedEvent.name}
          autoStart={isYoungLearnerGrade(gradeLevel)}
          // 2026-02-11c: Keep the visible paragraph and rendered
          // text in lock-step with whatever the AI is reading. Fires
          // an event the LessonViewer listens to so it can jump
          // currentParagraph + scroll the section into view.
          onSectionStart={(idx) => {
            try {
              window.dispatchEvent(
                new CustomEvent("ylc:teacher-section-start", { detail: { idx } })
              );
            } catch {
              /* non-fatal */
            }
          }}
        />
      </>
    );
  }

  // Render event list
  return (
    <div
      data-testid="event-curriculum-container"
      style={{
        background: "linear-gradient(135deg, #1a1a2e 0%, #0a0a1a 100%)",
        border: "2px solid #FFD700",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(90deg, #FFD70020 0%, transparent 100%)",
          padding: "20px",
          borderBottom: "1px solid #FFD70040",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2
              style={{
                color: "#FFD700",
                margin: 0,
                fontSize: "1.3rem",
                fontFamily: "Georgia, serif",
              }}
            >
              📜 Living School Curriculum
            </h2>
            <p style={{ color: "#aaa", margin: "5px 0 0", fontSize: "0.85rem" }}>
              Event-Triggered Lessons • Grade Level: {gradeLevel}
            </p>
          </div>
          {biblicalToday ? (
            <div style={{ textAlign: "right" }} data-testid="lsc-today-header">
              <div style={{ color: "#90EE90", fontSize: "0.85rem", fontWeight: "bold" }}>
                Day {biblicalToday.day} of {biblicalToday.month_name}
              </div>
              <div style={{ color: "#666", fontSize: "0.75rem" }}>
                {biblicalToday.gregorian}
              </div>
            </div>
          ) : currentDate && (
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#90EE90", fontSize: "0.85rem" }}>
                {currentDate.biblical}
              </div>
              <div style={{ color: "#666", fontSize: "0.75rem" }}>
                {currentDate.gregorian}
              </div>
            </div>
          )}
        </div>
        {/* 2026-02-21 user mandate: Living School needs explicit
            navigation. Header button mirrors the Celestial Observatory
            pattern so students never feel trapped. */}
        {onBack && (
          <div style={{ marginTop: "12px", display: "flex", justifyContent: "flex-end" }}>
            <button
              data-testid="living-school-back-btn"
              onClick={onBack}
              style={{
                background: "transparent",
                color: "#FFD700",
                border: "1px solid #FFD70080",
                borderRadius: "6px",
                padding: "6px 14px",
                fontSize: "0.8rem",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              {backLabel}
            </button>
          </div>
        )}
      </div>

      {/* DATA-DRIVEN LUNAR-EVENT ALERT CARD
          When today maps to a special protocol day (Day 1 New Moon, Day 14
          Full Moon Witness, Sabbath, Day-31 Intercalary), the backend's
          `sabbath.lunar_event` returns a {kind,title,subtitle,references[]}
          object. We render it as a centered dark-blue alert with the
          scriptural reference chips. On non-special days `lunar_event` is
          null and this whole block is hidden. */}
      {lunarEvent && (
        <div
          data-testid={`lunar-event-card-${lunarEvent.kind}`}
          style={{
            margin: "15px 20px 0",
            padding: "18px",
            background:
              lunarEvent.kind === "full_moon"
                ? "linear-gradient(135deg, #1a2540 0%, #0e1830 100%)"
                : lunarEvent.kind === "new_moon"
                ? "linear-gradient(135deg, #16203b 0%, #0a1228 100%)"
                : lunarEvent.kind === "intercalary"
                ? "linear-gradient(135deg, #3a2540 0%, #1f1230 100%)"
                : "linear-gradient(135deg, #1f3320 0%, #0f1f10 100%)",
            border: `2px solid ${
              lunarEvent.kind === "full_moon"
                ? "#7ddcff"
                : lunarEvent.kind === "sabbath"
                ? "#90EE90"
                : "#FFD700"
            }`,
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "8px",
          }}
        >
          <div style={{ fontSize: "2.2rem" }} aria-hidden>
            {lunarEvent.icon === "sun"
              ? "☀️"
              : lunarEvent.icon === "gate_open"
              ? "⛩"
              : lunarEvent.icon === "portal"
              ? "🌀"
              : "🌙"}
          </div>
          <div
            data-testid="lunar-event-title"
            style={{ color: "#FFD700", fontSize: "1.15rem", fontWeight: "bold", letterSpacing: "0.02em" }}
          >
            {lunarEvent.title}
          </div>
          <div
            data-testid="lunar-event-subtitle"
            style={{ color: "#cce4ff", fontSize: "0.85rem" }}
          >
            {lunarEvent.subtitle}
          </div>
          {Array.isArray(lunarEvent.references) && lunarEvent.references.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
                justifyContent: "center",
                marginTop: "6px",
              }}
              data-testid="lunar-event-refs"
            >
              {lunarEvent.references.map((r) => (
                <span
                  key={r.id}
                  style={{
                    background: "rgba(255, 215, 0, 0.12)",
                    color: "#FFD700",
                    border: "1px solid #FFD70060",
                    borderRadius: "999px",
                    padding: "4px 12px",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                  }}
                >
                  {r.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Awaiting-Spring-Witness banner — fires only at the end-of-364-day hold */}
      {biblicalToday?.awaiting_spring_witness && (
        <div
          data-testid="awaiting-spring-witness"
          style={{
            margin: "10px 20px 0",
            padding: "12px 14px",
            background: "linear-gradient(90deg, #3a2010 0%, #1a0f08 100%)",
            border: "1px solid #FFA500",
            borderRadius: "8px",
            color: "#FFD700",
            textAlign: "center",
            fontSize: "0.85rem",
          }}
        >
          ⏳ <strong>Awaiting Spring Witness</strong> — Day 364 has passed.
          The calendar pauses until the human-visible Spring New Moon is
          confirmed. Day 1 will not roll over automatically.
        </div>
      )}

      {/* Description */}
      <div
        style={{
          padding: "15px 20px",
          background: "#0a0a0a",
          borderBottom: "1px solid #333",
        }}
      >
        <p style={{ color: "#ccc", margin: 0, fontSize: "0.85rem" }}>
          <strong style={{ color: "#FFD700" }}>Living School:</strong> Lessons
          unlock based on calendar proximity to Biblical Appointed Times,
          celestial events, and dates requiring historical exposure. No
          pre-loaded content - everything is event-triggered.
        </p>
      </div>

      {/* Events List */}
      <div style={{ padding: "20px" }}>
        {events.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px" }}>
            <div style={{ fontSize: "3rem", marginBottom: "15px" }}>🌙</div>
            <p style={{ color: "#888" }}>
              No events currently unlocked. Check back as appointed times
              approach.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onSelect={() => handleEventSelect(event)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "12px 20px",
          background: "#050505",
          borderTop: "1px solid #333",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <span style={{ color: "#666", fontSize: "0.75rem" }}>
          Enoch 72-74 • Leviticus 23 Protocol
        </span>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {onBack && (
            <button
              data-testid="living-school-back-bottom-btn"
              onClick={onBack}
              style={{
                background: "transparent",
                border: "1px solid #FFD70080",
                color: "#FFD700",
                cursor: "pointer",
                fontSize: "0.8rem",
                padding: "6px 12px",
                borderRadius: "6px",
                fontWeight: "bold",
              }}
            >
              {backLabel}
            </button>
          )}
          <button
            onClick={fetchUnlockedEvents}
            style={{
              background: "transparent",
              border: "none",
              color: "#FFD700",
              cursor: "pointer",
              fontSize: "0.8rem",
            }}
          >
            ↻ Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

// Event Card Component
function EventCard({ event, onSelect }) {
  const style = CATEGORY_STYLES[event.category] || CATEGORY_STYLES.restoration;
  const urgencyColor = URGENCY_COLORS[event.urgency] || URGENCY_COLORS.normal;

  return (
    <button
      data-testid={`event-card-${event.id}`}
      onClick={onSelect}
      style={{
        background: style.bg,
        border: `2px solid ${style.border}`,
        borderRadius: "10px",
        padding: "15px",
        textAlign: "left",
        cursor: "pointer",
        transition: "all 0.2s ease",
        width: "100%",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "translateX(5px)";
        e.currentTarget.style.boxShadow = `0 4px 15px ${style.border}40`;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "translateX(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}
          >
            <span style={{ fontSize: "1.3rem" }}>{style.icon}</span>
            <span
              style={{
                background: `${style.border}30`,
                color: style.border,
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "0.7rem",
                textTransform: "uppercase",
              }}
            >
              {style.label}
            </span>
            <span
              style={{
                background: `${urgencyColor}30`,
                color: urgencyColor,
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "0.7rem",
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
            >
              {event.urgency}
            </span>
          </div>

          <h3
            style={{
              color: "#fff",
              margin: "0 0 5px",
              fontSize: "1.1rem",
            }}
          >
            {event.name}
          </h3>

          <p
            style={{
              color: "#ccc",
              margin: "0 0 10px",
              fontSize: "0.85rem",
            }}
          >
            {event.focus}
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
            {event.primary_sources?.slice(0, 3).map((source, idx) => (
              <span
                key={idx}
                style={{
                  background: "#ffffff10",
                  color: "#aaa",
                  padding: "2px 6px",
                  borderRadius: "3px",
                  fontSize: "0.7rem",
                }}
              >
                {source}
              </span>
            ))}
            {event.primary_sources?.length > 3 && (
              <span style={{ color: "#666", fontSize: "0.7rem" }}>
                +{event.primary_sources.length - 3} more
              </span>
            )}
          </div>
        </div>

        <div style={{ textAlign: "right", marginLeft: "15px" }}>
          <div
            style={{
              color: urgencyColor,
              fontSize: "1.5rem",
              fontWeight: "bold",
            }}
          >
            {event.days_until <= 0 ? "NOW" : event.days_until}
          </div>
          <div style={{ color: "#888", fontSize: "0.7rem" }}>
            {event.days_until <= 0 ? "Active" : "days"}
          </div>
          {event.biblical_date && (
            <div style={{ color: "#666", fontSize: "0.65rem", marginTop: "5px" }}>
              {event.biblical_date}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// Lesson Viewer Component
function LessonViewer({ lesson, event, gradeLevel, onBack, loading }) {
  const [currentParagraph, setCurrentParagraph] = useState(0);
  const [showEngagement, setShowEngagement] = useState(false);
  const [engagementResponse, setEngagementResponse] = useState("");
  // 2026-02-11: standardized 5-button action bar for Living School
  // lessons (parity with K-12 Academic Foundations + Zion Pillars).
  const [restartKey, setRestartKey] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const lessonBodyRef = useRef(null);

  // ── Audio ↔ Visible sync (user mandate 2026-02-11c) ──
  // When the InvisibleTeacher starts reading a section it emits a
  // window event with the section index. We jump the visible
  // paragraph counter to match so the student always SEES exactly
  // what's being READ — eliminating the "Reading section 15 / showing
  // paragraph 2" confusion the user reported.
  useEffect(() => {
    const handler = (e) => {
      const idx = e?.detail?.idx;
      if (typeof idx !== "number") return;
      setCurrentParagraph(idx);
      setShowEngagement(false);
      setEngagementResponse("");
      // Scroll the now-active paragraph into view.
      if (typeof window !== "undefined") {
        const node = document.querySelector('[data-testid="lesson-active-paragraph"]');
        if (node && typeof node.scrollIntoView === "function") {
          node.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    };
    window.addEventListener("ylc:teacher-section-start", handler);
    return () => window.removeEventListener("ylc:teacher-section-start", handler);
  }, []);

  const style = CATEGORY_STYLES[event.category] || CATEGORY_STYLES.restoration;
  const paragraphs = lesson?.content?.paragraphs || [];
  const totalParagraphs = paragraphs.length;

  // Handle next paragraph
  const handleNext = () => {
    if (currentParagraph < totalParagraphs - 1) {
      setCurrentParagraph((prev) => prev + 1);
      setShowEngagement(false);
      setEngagementResponse("");
    }
  };

  // Handle engagement response
  const handleEngagementSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/event-curriculum/lesson/${event.id}/progress`,
        null,
        {
          params: {
            paragraph_index: currentParagraph,
            engagement_response: engagementResponse,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (err) {
      console.error("Failed to record progress:", err);
    }

    handleNext();
  };

  if (loading) {
    return (
      <div
        style={{
          background: style.bg,
          border: `2px solid ${style.border}`,
          borderRadius: "12px",
          padding: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: "10px" }}>{style.icon}</div>
        <p style={{ color: style.border }}>Loading Lesson...</p>
      </div>
    );
  }

  const currentPara = paragraphs[currentParagraph];
  const hasEngagement = currentPara?.has_engagement;

  return (
    <div
      data-testid="lesson-viewer"
      style={{
        background: style.bg,
        border: `2px solid ${style.border}`,
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Lesson body wrapper — captures all teaching content for PDF
          export. Restart key bumps to remount fresh. */}
      <div ref={lessonBodyRef} key={restartKey}>
      {/* Header */}
      <div
        style={{
          background: `linear-gradient(90deg, ${style.border}20 0%, transparent 100%)`,
          padding: "15px 20px",
          borderBottom: `1px solid ${style.border}40`,
          display: "flex",
          alignItems: "center",
          gap: "15px",
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "transparent",
            border: "none",
            color: style.border,
            cursor: "pointer",
            fontSize: "1.2rem",
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <h3 style={{ color: "#fff", margin: 0, fontSize: "1.1rem" }}>
            {lesson?.event_name}
          </h3>
          <p style={{ color: "#aaa", margin: "3px 0 0", fontSize: "0.8rem" }}>
            {lesson?.event_focus}
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: style.border, fontSize: "0.9rem", fontWeight: "bold" }}>
            {currentParagraph + 1} / {totalParagraphs}
          </div>
          <div style={{ color: "#666", fontSize: "0.7rem" }}>paragraphs</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ height: "4px", background: "#333" }}>
        <div
          style={{
            height: "100%",
            width: `${((currentParagraph + 1) / totalParagraphs) * 100}%`,
            background: style.border,
            transition: "width 0.3s ease",
          }}
        />
      </div>

      {/* Content */}
      <div style={{ padding: "25px" }}>
        {currentPara && (
          <div
            data-testid="lesson-active-paragraph"
            style={{
              color: "#eee",
              fontSize: "1rem",
              lineHeight: "1.8",
              whiteSpace: "pre-wrap",
            }}
          >
            {currentPara.text}
          </div>
        )}

        {/* Engagement Section */}
        {hasEngagement && !showEngagement && (
          <div
            style={{
              marginTop: "25px",
              padding: "15px",
              background: `${style.border}10`,
              border: `1px solid ${style.border}40`,
              borderRadius: "8px",
            }}
          >
            <button
              onClick={() => setShowEngagement(true)}
              style={{
                width: "100%",
                padding: "12px",
                background: `linear-gradient(135deg, ${style.border} 0%, ${style.border}CC 100%)`,
                border: "none",
                borderRadius: "6px",
                color: "#000",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              ⏸ Pause - Respond to Continue
            </button>
          </div>
        )}

        {showEngagement && (
          <div
            style={{
              marginTop: "25px",
              padding: "20px",
              background: `linear-gradient(135deg, ${style.border}20 0%, ${style.border}05 100%)`,
              border: `2px solid ${style.border}`,
              borderRadius: "10px",
            }}
          >
            <div
              style={{
                color: style.border,
                fontWeight: "bold",
                marginBottom: "15px",
                fontSize: "0.9rem",
              }}
            >
              YOUR RESPONSE
            </div>
            <textarea
              value={engagementResponse}
              onChange={(e) => setEngagementResponse(e.target.value)}
              placeholder="Share your thoughts or answer the question above..."
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "12px",
                background: "#0a0a0a",
                border: "1px solid #333",
                borderRadius: "6px",
                color: "#fff",
                fontSize: "0.95rem",
                resize: "vertical",
              }}
            />
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "15px",
              }}
            >
              <button
                onClick={handleEngagementSubmit}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: style.border,
                  border: "none",
                  borderRadius: "6px",
                  color: "#000",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Submit & Continue →
              </button>
              <button
                onClick={handleNext}
                style={{
                  padding: "12px 20px",
                  background: "transparent",
                  border: `1px solid ${style.border}`,
                  borderRadius: "6px",
                  color: style.border,
                  cursor: "pointer",
                }}
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {/* Navigation */}
        {!hasEngagement && currentParagraph < totalParagraphs - 1 && (
          <div style={{ marginTop: "25px", textAlign: "center" }}>
            <button
              onClick={handleNext}
              style={{
                padding: "12px 30px",
                background: `linear-gradient(135deg, ${style.border} 0%, ${style.border}CC 100%)`,
                border: "none",
                borderRadius: "6px",
                color: "#000",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Continue →
            </button>
          </div>
        )}

        {/* Completion */}
        {currentParagraph === totalParagraphs - 1 && !showEngagement && (
          <div
            style={{
              marginTop: "25px",
              padding: "20px",
              background: "linear-gradient(135deg, #1a3a1a 0%, #0a2a0a 100%)",
              border: "2px solid #90EE90",
              borderRadius: "10px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>✓</div>
            <h4 style={{ color: "#90EE90", margin: "0 0 10px" }}>
              Lesson Complete!
            </h4>
            <p style={{ color: "#ccc", margin: 0, fontSize: "0.9rem" }}>
              You have completed this event-triggered lesson.
            </p>
            <button
              onClick={onBack}
              style={{
                marginTop: "15px",
                padding: "10px 20px",
                background: "#90EE90",
                border: "none",
                borderRadius: "6px",
                color: "#000",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Back to Events
            </button>
          </div>
        )}
      </div>

      {/* Primary Sources Footer */}
      <div
        style={{
          padding: "12px 20px",
          background: "#050505",
          borderTop: "1px solid #333",
        }}
      >
        <div style={{ color: "#666", fontSize: "0.7rem", marginBottom: "5px" }}>
          PRIMARY SOURCES:
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
          {lesson?.primary_sources?.map((source, idx) => (
            <span
              key={idx}
              style={{
                background: "#ffffff10",
                color: "#aaa",
                padding: "2px 6px",
                borderRadius: "3px",
                fontSize: "0.7rem",
              }}
            >
              {source}
            </span>
          ))}
        </div>
      </div>
      </div>

      {/* Standardized lesson action bar — identical 5-button set
          across K-12 Academic Foundations, Zion Pillars, and
          Living School (Mo'adim / Pagan-Holiday / Celestial /
          Identity events). User mandate 2026-02-11. */}
      <StandardLessonActions
        onBack={onBack}
        onRestart={() => {
          setCurrentParagraph(0);
          setShowEngagement(false);
          setEngagementResponse("");
          setRestartKey((k) => k + 1);
          // Reset the InvisibleTeacher (rendered as a sibling, not a
          // child — so a React key bump can't reach it). The teacher
          // listens for this event and resets to section 0 + kills audio.
          try {
            window.dispatchEvent(new CustomEvent("ylc:teacher-restart"));
          } catch {
            /* non-fatal */
          }
          if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }}
        onTakeQuiz={async () => {
          try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
              `${API}/event-curriculum/lesson/${event.id}/quiz`,
              null,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const q = res.data?.questions?.[0]?.question || "Quiz generated.";
            alert(`📝 Living School Quiz\n\n${q}`);
          } catch {
            alert("Quiz unavailable for this lesson — please try again shortly.");
          }
        }}
        onDownloadPDF={async () => {
          if (!lesson) return;
          setPdfLoading(true);
          try {
            const { exportLessonPDF } = await import("../utils/pdfExport");
            await exportLessonPDF({
              title: event?.name || "Living School Lesson",
              subtitle: `Living School · ${event?.category || "Event"}`,
              bodyNode: lessonBodyRef.current,
              gradeLevel,
            });
          } catch (e) {
            console.error("PDF export failed:", e);
            alert("PDF export failed. Please try again.");
          } finally {
            setPdfLoading(false);
          }
        }}
        pdfLoading={pdfLoading}
      />
    </div>
  );
}

export default EventTriggeredCurriculum;
